import React, {FC, useCallback, useMemo, useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl
} from "@mui/material";
import {AsyncFormSelect, FormDateRange, FormSelect, useDescriptionAlert} from "../../../../share";
import {t} from "@lingui/core/macro";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {emptyListPaginationResult, listPaginationLimits} from "../../../../types/global.ts";
import {useGetPricingProfilePresentationQuery, useInsertPricingProfileMutation} from "../../../apis/PricingProfile.api.ts";
import {toString as toStringPricingProfile} from "../../../types/models/PricingProfile.ts";
import {
    useDeletePriceMutation,
    useGetPriceAdvancedPresentationQuery,
    useInsertPriceMutation,
    useUpdatePriceMutation
} from "../../../apis/Price.api.ts";
import {PrimaryPrice} from "../../../types/models/Price.ts";
import {createWoodGroupsList, fetchWoodData, WoodGroupsList} from "../../../types/models/MergeGroups.ts";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import {Trans} from "@lingui/react/macro";
import {array, date, object, string, TypeOf} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {formatDateTimeParam} from "../../../../utils/formatDate.ts";
import useSearchDebounce from "../../../../hooks/useSearchDebounce.ts";
import {useGetCuttingPresentationQuery, useGetCuttingQuery} from "../../../apis/Cutting.api.ts";
import {useGetTimberStoragePresentationQuery} from "../../../apis/TimberStorage.api.ts";
import {useGetTimberTypePresentationQuery} from "../../../apis/TimberType.api.ts";
import {useGetTimberClassPresentationQuery} from "../../../apis/TimberClass.api.ts";
import {
    useGetGroupQuery as useGetLogGroupQuery,
} from "../../../apis/Log.api.ts";
import {
    useGetGroupQuery as useGetCordwoodGroupQuery,
} from "../../../apis/Cordwood.api.ts";
import {PrimaryCutting, toString as toStringCutting} from "../../../types/models/Cutting.ts";
import {useDispatch} from "react-redux";
import {styled} from "@mui/material/styles";
import {tableCellClasses} from "@mui/material/TableCell";
import {defaultFormOptions} from "../report/GenericRootReport.tsx";
import {ShipmentTypePairs, toString as toStringShipmentType} from "../../../types/models/ShipmentType.d";
import CuttingName, {CuttingNameDisplay} from "../../cutting/CuttingDetail/CuttingName.tsx";
import * as crudgen from "../../../../share";
import {toString as toStringTimberStorage} from "../../../types/models/TimberStorage.ts";
import TimberStorageName, {
    TimberStorageNameDisplay
} from "../../timberStorage/TimberStorageDetail/TimberStorageName.tsx";
import {toString as toStringTimberType} from "../../../types/models/TimberType.ts";
import TimberTypeName, {TimberTypeNameDisplay} from "../../timberType/TimberTypeDetail/TimberTypeName.tsx";
import {toString as toStringTimberClass} from "../../../types/models/TimberClass.ts";
import TimberClassName, {TimberClassNameDisplay} from "../../timberClass/TimberClassDetail/TimberClassName.tsx";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";
import NumberField from "../../../../share/components/form/FormInput/NumberField.tsx";

const StyledTableCell = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.head}`]: {
        fontWeight: 'bold',
    },
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: theme.palette.action.hover,
        fontSize: 14,
    },
}));

const StyledTableTypeClass = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        borderBottom: 'none',
    },
}));

const StyledTableStorage = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        fontWeight: 'bold',
        borderBottom: 'none',
    },
}));

const StyledTableStorageTotal = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: theme.palette.action.hover,
        fontWeight: 'bold',
    },
}));

const StyledTableFooter = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: theme.palette.action.hover,
        fontWeight: 'bold',
        fontSize: '1em',
    },
}));

const initialFormState = {
    shipmentType: '',
    typeId: [],
    classId: [],
    cuttingId: [],
    storageId: [],
    startCreate: null,
    endCreate: null,
    startShipment: null,
    endShipment: null,
    pricingProfileId: null,
}

const formSchema = object({
    shipmentType: string().nullish(),
    typeId: array(string()).nullish(),
    classId: array(string()).nullish(),
    cuttingId: array(string()).nullish(),
    storageId: array(string()).nullish(),
    startCreate: date().nullish(),
    endCreate: date().nullish(),
    startShipment: date().nullish(),
    endShipment: date().nullish(),
    pricingProfileId: string().nullish(),
});

export type FormInput = TypeOf<typeof formSchema>;

type FormOptions = {
    showShipmentType: boolean;
    showCutting: boolean;
    showStorage: boolean;
    showCreateDate: boolean;
    showShipmentDate: boolean;
}

type PriceCalculatorProps = {
    shipmentDefined: boolean | null;
    formOptions: FormOptions;
    // The filter goes straight to the group-by endpoints, which take lists
    // for the things they filter on, so it is not a flat record of strings.
    customizeFiler: (filter: Record<string, any | undefined>) => Record<string, any | undefined>;
    /**
     * The state the form starts in.
     *
     * The dates have to be `Date` objects: the form is checked against a
     * schema that wants real dates, and the query is built from them.
     */
    initialState?: FormInput;
}

const NumericPriceInput: FC = ({value, onChange, originalValue}) => (
    <NumberField
        size="small"
        min={0}
        value={value}
        onValueCommitted={onChange}
        label={originalValue && originalValue !== value ? t`Price [Original: ${originalValue}]` : t`Price`}
    />
);

const PriceCalculator: FC<PriceCalculatorProps> = ({
                                                       shipmentDefined,
                                                       formOptions = defaultFormOptions,
                                                       customizeFiler,
                                                       initialState = initialFormState,
}) => {

    const dispatch = useDispatch()

    const {openFailAlert} = useDescriptionAlert()

    const methods = useForm<FormInput>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            ...initialFormState,
            ...initialState,
        }
    });

    const [groupedData, setGroupedData] = useState<WoodGroupsList>(createWoodGroupsList())

    const [classId, typeId, cuttingId, storageId, shipmentType, startCreate, endCreate, startShipment, endShipment,
        selectedProfileId] = useWatch({
        control: methods.control,
        name: ['classId', 'typeId', 'cuttingId', 'storageId', 'shipmentType', 'startCreate', 'endCreate', 'startShipment', 'endShipment', 'pricingProfileId']
    })

    const filter = customizeFiler({
        classId,
        typeId,
        cuttingId,
        storageId,
        startCreate: formatDateTimeParam(startCreate),
        endCreate: formatDateTimeParam(endCreate),
        startShipment: formatDateTimeParam(startShipment),
        endShipment: formatDateTimeParam(endShipment),
        shipmentDefined,
    })

    const [, setCuttingInputValue, cuttingInputTerm] = useSearchDebounce();
    const {
        data: cuttingList = emptyListPaginationResult,
        isLoading: getCuttingLoading,
        isFetching: getCuttingFetching,
    } = useGetCuttingPresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: cuttingInputTerm,
        },
    }, {skip: !formOptions.showCutting})

    const [, setStorageInputValue, storageInputTerm] = useSearchDebounce();
    const {
        data: storageList = emptyListPaginationResult,
        isLoading: getStorageLoading,
        isFetching: getStorageFetching,
    } = useGetTimberStoragePresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: storageInputTerm,
            cuttingIdRefs: filter.cuttingId,
        },
    }, {skip: !formOptions.showStorage})

    const [, setTimberTypeInputValue, timberTypeInputTerm] = useSearchDebounce();
    const {
        data: timberTypeList = emptyListPaginationResult,
        isLoading: getTimberTypeListLoading,
        isFetching: getTimberTypeListFetching,
    } = useGetTimberTypePresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: timberTypeInputTerm,
        },
    })

    const [, setTimberClassInputValue, timberClassInputTerm] = useSearchDebounce();
    const {
        data: timberClassList = emptyListPaginationResult,
        isLoading: getTimberClassListLoading,
        isFetching: getTimberClassListFetching,
    } = useGetTimberClassPresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: timberClassInputTerm,
        },
    })

    const logData = useGetLogGroupQuery(filter, {skip: shipmentType == 'Cordwood'})
    const cordwoodData = useGetCordwoodGroupQuery(filter, {skip: shipmentType == 'Logs'})
    const {data: selectedCutting} = useGetCuttingQuery(PrimaryCutting.fromId(cuttingId || filter.cuttingId),
        {
            skip: !(cuttingId || filter.cuttingId) || Array.isArray(cuttingId || filter.cuttingId)
        }
    )

    React.useEffect(() => {
        let isSubscribed = true;

        if (!(logData.isFetching || cordwoodData.isFetching)) {
            fetchWoodData(
                dispatch,
                shipmentType == 'Cordwood' ? [] : logData.data,
                shipmentType == 'Logs' ? [] : cordwoodData.data,
            )
                .then(result => {
                    if (isSubscribed) {
                        result.isLoading = false
                        setGroupedData(result)
                    }
                })
                .catch(console.error)
        }

        if (logData.isError || cordwoodData.isError) {
            openFailAlert(t`Failed to load data`)
        }

        return () => isSubscribed = false;
    }, [shipmentType, logData, cordwoodData,]);

    const [profileInput, setProfileInput] = useState<string>("");

    const {data: pricingProfiles = {items: [], totalItems: 0, page: 0, pageSize: 0, numPages: 0}, isLoading: profileLoading, isFetching: profileFetching} = useGetPricingProfilePresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: profileInput,
            nameOr: false,
            orderBys: [],
            caseSensitive: false,
        }
    });

    const priceQuery = useGetPriceAdvancedPresentationQuery({
        list: {page: 0, pageSize: 10000},
        filter: {
            pricingProfileIdRefs: selectedProfileId ? [selectedProfileId as any] : [],
            orderBys: [],
            caseSensitive: false,
        }
    }, {skip: !selectedProfileId});

    // Local overrides keyed by `${typeId}|${classId}|${thicknessId?}`
    const [overrides, setOverrides] = useState<Record<string, number | undefined>>({});

    // Original prices map (fetched from API)
    // Map of existing Price entity ids to support updates (keyed same way)
    const [basePriceMap, basePriceIdMap] = useMemo(() => {
        setOverrides({});
        const map1 = new Map<string, number>();
        const map2 = new Map<string, string>();
        const items = priceQuery.data?.items || [];
        for (const p of items as any[]) {
            const thicknessKey = (p.thicknessClassId ?? "").toString();
            const key = `${p.timberTypeId}|${p.classId}|${thicknessKey}`;
            map1.set(key, p.price);
            map2.set(key, p.id);
        }
        return [map1, map2];
    }, [priceQuery.data]);

    // Effective price map: overrides win; otherwise base
    const priceMap = useMemo(() => {
        const effective = new Map<string, number>();
        // Start with base
        for (const [k, v] of basePriceMap.entries()) effective.set(k, v);
        // Apply overrides
        for (const [k, v] of Object.entries(overrides)) {
            if (v != null && !Number.isNaN(v)) effective.set(k, v);
            if (v == null) effective.delete(k);
        }
        return effective;
    }, [basePriceMap, overrides]);

    const {totalAmount, matchedCount, unmatchedCount} = useMemo(() => {
        let total = 0;
        let matched = 0;
        let unmatched = 0;

        for (const cuttingGroup of groupedData.items as any[]) {
            for (const storageGroup of cuttingGroup.items as any[]) {
                if (!storageGroup?.items) continue;

                let storageAmount = 0;

                for (const clsGroup of storageGroup.items as any[]) {
                    const timberTypeId = clsGroup?.timberType?.id;
                    const classId = clsGroup?.timberClass?.id;

                    // Logs have thickness groups array; cordwood class group has no nested items with thickness
                    const isLogs = Array.isArray(clsGroup.items) && clsGroup.items.length > 0 && !!clsGroup.items[0]?.thicknessClass;
                    if (isLogs) {
                        let clsAmount = 0;
                        for (const thickGroup of clsGroup.items as any[]) {
                            const thicknessClassId = thickGroup?.thicknessClass?.id;
                            const volume = thickGroup?.sum || 0;
                            const key = `${timberTypeId}|${classId}|${(thicknessClassId ?? "").toString()}`;
                            const price = priceMap.get(key);
                            if (price != null) {
                                const amount = price * volume;
                                total += amount;
                                matched += 1;
                                storageAmount += amount;
                                clsAmount += amount;

                                thickGroup.price = price
                                thickGroup.amount = amount
                            } else {
                                unmatched += 1;
                            }

                            thickGroup.key = key
                        }
                        clsGroup.amount = clsAmount
                    } else {
                        // Cordwood: try to match where thickness is null/empty in price
                        const volume = clsGroup?.sum || 0;
                        const key = `${timberTypeId}|${classId}|`;
                        const price = priceMap.get(key);
                        if (price != null) {
                            const amount = price * volume;
                            total += amount;
                            matched += 1;
                            storageAmount += amount;

                            clsGroup.price = price
                            clsGroup.amount = amount
                        } else {
                            unmatched += 1;
                        }

                        clsGroup.key = key
                    }
                }
                storageGroup.amount = storageAmount
            }
        }

        return {totalAmount: total, matchedCount: matched, unmatchedCount: unmatched};
    }, [groupedData, priceMap]);

    // Handlers to change row price
    const handlePriceChange = useCallback((key: string, newVal: string) => {
        const parsed = newVal === '' ? undefined : Number(newVal);
        setOverrides(prev => ({...prev, [key]: parsed}));
    });

    const [newTotalAmount, setNewTotalAmount] = useState<number | undefined>();

    const reCalculatePrices = useCallback(async () => {
        if (newTotalAmount) {
            const newOverrides = {...overrides};
            const factor = newTotalAmount / totalAmount;

            for (const cuttingGroup of groupedData.items as any[]) {
                for (const storageGroup of cuttingGroup.items as any[]) {
                    if (!storageGroup?.items) continue;

                    for (const clsGroup of storageGroup.items as any[]) {
                        const timberTypeId = clsGroup?.timberType?.id;
                        const classId = clsGroup?.timberClass?.id;

                        // Logs have thickness groups array; cordwood class group has no nested items with thickness
                        const isLogs = Array.isArray(clsGroup.items) && clsGroup.items.length > 0 && !!clsGroup.items[0]?.thicknessClass;
                        if (isLogs) {
                            for (const thickGroup of clsGroup.items as any[]) {
                                const thicknessClassId = thickGroup?.thicknessClass?.id;
                                const volume = thickGroup?.sum || 0;
                                const key = `${timberTypeId}|${classId}|${(thicknessClassId ?? "").toString()}`;
                                const price = priceMap.get(key);
                                if (price != null) {
                                    const newAmount = price * volume * factor;
                                    newOverrides[key] = Math.round(newAmount * 100 / volume) / 100;
                                }
                            }
                        } else {
                            // Cordwood: try to match where thickness is null/empty in price
                            const volume = clsGroup?.sum || 0;
                            const key = `${timberTypeId}|${classId}|`;
                            const price = priceMap.get(key);
                            if (price != null) {
                                const newAmount = price * volume * factor;
                                // round value to 2 decimal places
                                newOverrides[key] = Math.round(newAmount * 100 / volume) / 100;
                            }
                        }
                    }
                }
            }

            setOverrides({...newOverrides});
        }
    }, [newTotalAmount, overrides, totalAmount]);

    // Save actions
    const [insertProfile] = useInsertPricingProfileMutation();
    const [insertPrice] = useInsertPriceMutation();
    const [updatePrice] = useUpdatePriceMutation();
    const [deletePrice] = useDeletePriceMutation();

    const saveChangedToSelectedProfile = useCallback(async () => {
        if (!selectedProfileId) return;
        const changes = Object.entries(overrides);
        for (const [key, val] of changes) {
            const [typeId, classId, thicknessId] = key.split('|');
            const existingId = basePriceIdMap.get(key);
            if (existingId && val != null && !Number.isNaN(val as number)) {
                await updatePrice({
                    // The update endpoint builds its path from the primary
                    // key, so it needs the key itself and not a plain object.
                    id: PrimaryPrice.fromId(existingId),
                    data: {
                        timberTypeId: typeId as any,
                        classId: classId as any,
                        thicknessClassId: (thicknessId || undefined) as any,
                        price: val as number,
                    }
                });
            } else if (existingId && (val == null || Number.isNaN(val as number))) {
                await deletePrice({id: existingId});
            } else if (val != null && !Number.isNaN(val as number)) {
                await insertPrice({
                    pricingProfileId: selectedProfileId as any,
                    timberTypeId: typeId as any,
                    classId: classId as any,
                    thicknessClassId: (thicknessId || undefined) as any,
                    price: val as number,
                });
            }
        }
    }, [selectedProfileId, overrides, basePriceIdMap, updatePrice, insertPrice, deletePrice]);

    // Dialog state for creating new profile
    const [newProfileOpen, setNewProfileOpen] = useState(false);
    const [newProfileName, setNewProfileName] = useState("");
    const [newProfileDescription, setNewProfileDescription] = useState("");

    const saveAsNewProfile = useCallback(async () => {
        if (!newProfileName.trim()) return;
        // Create profile
        const result = await insertProfile({ name: newProfileName.trim(), description: newProfileDescription || undefined } as any).unwrap();
        const newProfileId = (result as any)?.id;
        // We will persist effective prices for all visible rows that have a price (override or base)
        const persistedKeys = new Set<string>();

        const saveValue = async (r: string) => {
            if (persistedKeys.has(r)) return;
            const priceVal: number | undefined = overrides[r] ?? basePriceMap.get(r);
            if (priceVal != null) {
                const [typeId, classId, thicknessId] = r.split('|');
                await insertPrice({
                    pricingProfileId: newProfileId as any,
                    timberTypeId: typeId as any,
                    classId: classId as any,
                    thicknessClassId: (thicknessId || undefined) as any,
                    price: priceVal,
                });
                persistedKeys.add(r);
            }
        }

        for (const r of Object.keys(overrides)) {
            await saveValue(r)
        }
        for (const r of basePriceMap.keys()) {
            await saveValue(r)
        }

        methods.setValue('pricingProfileId', newProfileId, {
            shouldValidate: true,
            shouldDirty: false
        })
        setNewProfileOpen(false);
        setNewProfileName("");
        setNewProfileDescription("");
    }, [methods, newProfileName, insertProfile, newProfileDescription, overrides, basePriceMap, insertPrice]);

    return (
        <Paper variant="outlined" sx={{p: 2}}>

            <Typography variant="h6">{t`Price Calculator`}</Typography>

            <Stack direction="column" spacing={3}>
                <FormProvider {...methods}>
                    <FormControl size="small">
                        {formOptions.showShipmentType &&
                            <FormSelect
                                key='shipmentType'
                                name='shipmentType'
                                label={t`Shipment Type`}
                                options={ShipmentTypePairs}
                                optionLabelMap={toStringShipmentType}
                                optionsLoading={false}
                            />
                        }
                        {formOptions.showCutting &&
                            <AsyncFormSelect
                                key='cuttingId'
                                name='cuttingId'
                                label={t`Cutting`}
                                options={cuttingList.items}
                                optionsLoading={getCuttingLoading || getCuttingFetching}
                                optionLabelMap={toStringCutting}
                                onInputChange={setCuttingInputValue}
                                renderOption={(optionProps, option, {inputValue}) => {
                                    const {key, ...filteredOptionProps} = optionProps;
                                    return (<li key={key} {...filteredOptionProps}><CuttingNameDisplay
                                        data={option}/></li>)
                                }}
                                renderValue={(value, getItemProps) =>
                                    value.map((option: any, index: number) => {
                                        const {key, ...tagProps} = getItemProps({index})
                                        return (
                                            <crudgen.SelectChip key={key} {...tagProps}>
                                                <CuttingName id={option}/>
                                            </crudgen.SelectChip>
                                        );
                                    })
                                }
                                multiple
                            />
                        }
                        {formOptions.showStorage &&
                            <AsyncFormSelect
                                key='storageId'
                                name='storageId'
                                label={t`Storage`}
                                options={storageList.items}
                                optionsLoading={getStorageLoading || getStorageFetching}
                                optionLabelMap={toStringTimberStorage}
                                onInputChange={setStorageInputValue}
                                renderOption={(optionProps, option, {inputValue}) => {
                                    const {key, ...filteredOptionProps} = optionProps;
                                    return (
                                        <li key={key} {...filteredOptionProps}><TimberStorageNameDisplay
                                            data={option}/></li>)
                                }}
                                renderValue={(value, getItemProps) =>
                                    value.map((option: any, index: number) => {
                                        const {key, ...tagProps} = getItemProps({index})
                                        return (
                                            <crudgen.SelectChip key={key} {...tagProps}>
                                                <TimberStorageName id={option}/>
                                            </crudgen.SelectChip>
                                        );
                                    })
                                }
                                multiple
                            />
                        }
                        <Stack direction="row" spacing={1}>
                            <AsyncFormSelect
                                key='typeId'
                                name='typeId'
                                label={t`Timber Type`}
                                options={timberTypeList.items}
                                optionsLoading={getTimberTypeListLoading || getTimberTypeListFetching}
                                optionLabelMap={toStringTimberType}
                                onInputChange={setTimberTypeInputValue}
                                renderOption={(optionProps, option, {inputValue}) => {
                                    const {key, ...filteredOptionProps} = optionProps;
                                    return (
                                        <li key={key} {...filteredOptionProps}><TimberTypeNameDisplay
                                            data={option}/></li>)
                                }}
                                renderValue={(value, getItemProps) =>
                                    value.map((option: any, index: number) => {
                                        const {key, ...tagProps} = getItemProps({index})
                                        return (
                                            <crudgen.SelectChip key={key} {...tagProps}>
                                                <TimberTypeName id={option}/>
                                            </crudgen.SelectChip>
                                        );
                                    })
                                }
                                multiple
                            />
                            <AsyncFormSelect
                                key='classId'
                                name='classId'
                                label={t`Timber Class`}
                                options={timberClassList.items}
                                optionsLoading={getTimberClassListLoading || getTimberClassListFetching}
                                optionLabelMap={toStringTimberClass}
                                onInputChange={setTimberClassInputValue}
                                renderOption={(optionProps, option, {inputValue}) => {
                                    const {key, ...filteredOptionProps} = optionProps;
                                    return (
                                        <li key={key} {...filteredOptionProps}><TimberClassNameDisplay
                                            data={option}/></li>)
                                }}
                                renderValue={(value, getItemProps) =>
                                    value.map((option: any, index: number) => {
                                        const {key, ...tagProps} = getItemProps({index})
                                        return (
                                            <crudgen.SelectChip key={key} {...tagProps}>
                                                <TimberClassName id={option}/>
                                            </crudgen.SelectChip>
                                        );
                                    })
                                }
                                multiple
                            />
                        </Stack>
                        {formOptions.showCreateDate &&
                            <FormDateRange
                                displayDateRange={ {
                                    startDate: selectedCutting?.startAt && new Date(selectedCutting?.startAt),
                                    endDate: !(selectedCutting?.endAt) || new Date(selectedCutting?.endAt) > new Date() ? new Date() : new Date(selectedCutting?.endAt)
                                } }
                                minDate={selectedCutting?.startAt}
                                maxDate={selectedCutting?.endAt}
                                startName="startCreate"
                                endName="endCreate"
                                startLabel={t`From`}
                                endLabel={t`To Timber Input Date`}
                            />}
                        {shipmentDefined && formOptions.showShipmentDate &&
                            <FormDateRange
                                displayDateRange={ {
                                    startDate: selectedCutting?.startAt && new Date(selectedCutting?.startAt),
                                    endDate: !(selectedCutting?.endAt) || new Date(selectedCutting?.endAt) > new Date() ? new Date() : new Date(selectedCutting?.endAt)
                                } }
                                minDate={selectedCutting?.startAt}
                                maxDate={selectedCutting?.endAt}
                                startName="startShipment"
                                endName="endShipment"
                                startLabel={t`From`}
                                endLabel={t`To Shipment Date`}
                            />}
                        <Divider sx={{m: 2}}/>
                        <FormProvider {...methods}>
                            <AsyncFormSelect
                                key='pricingProfileId'
                                name='pricingProfileId'
                                label={t`Pricing Profile`}
                                options={pricingProfiles.items}
                                optionsLoading={profileLoading || profileFetching}
                                optionLabelMap={toStringPricingProfile}
                                onInputChange={setProfileInput}
                                renderOption={(optionProps, option) => {
                                    const {key, ...filteredOptionProps} = optionProps as any;
                                    return (<li key={key} {...filteredOptionProps}>{toStringPricingProfile(option as any)}</li>)
                                }}
                            />
                            <Stack spacing={2} direction="row" divider={<Divider orientation="vertical" flexItem />} sx={{m: 1}}>
                                <Typography variant="body1" sx={{m: 1}}>{t`Matched rows`}: {matchedCount}</Typography>
                                <Typography variant="body1" sx={{ color: unmatchedCount > 0 ? 'warning.main' : undefined }}>{t`Unmatched rows`}: {unmatchedCount}</Typography>
                            </Stack>
                            <Box gap={1} sx={{ display: "flex", flexWrap: "wrap" }}>
                                <Button variant="contained" color="primary" disabled={!selectedProfileId} onClick={saveChangedToSelectedProfile}>
                                    <Trans>Save changes to selected profile</Trans>
                                </Button>
                                <Button variant="outlined" onClick={() => setNewProfileOpen(true)}>
                                    <Trans>Save as new profile</Trans>
                                </Button>
                            </Box>
                            <Divider sx={{m: 2}}/>
                        </FormProvider>
                    </FormControl>
                </FormProvider>
            </Stack>

            <Stack direction="column" spacing={2}>

            {!groupedData.items || groupedData.isLoading || logData.isLoading || logData.isFetching || logData.isError || cordwoodData.isLoading || cordwoodData.isFetching || cordwoodData.isError ?
                <Paper
                    sx={{padding: "20px", justifyContent: "center", alignItems: "center", display: "flex"}}>
                    <GradientCircularProgress/>
                </Paper>
                :

                <TableContainer>

                    <Table stickyHeader aria-label="sticky table" size="small">
                        <TableHead key="head">
                            <TableRow key="header">
                                <StyledTableCell key="storage"><Trans>Storage</Trans></StyledTableCell>
                                <StyledTableCell key="type"><Trans>Type</Trans></StyledTableCell>
                                <StyledTableCell key="class"><Trans>Class</Trans></StyledTableCell>
                                <StyledTableCell key="thickness-class"><Trans>Thickness
                                    Class</Trans></StyledTableCell>
                                <StyledTableCell key="count"
                                                 align="right"><Trans>Logs Count</Trans></StyledTableCell>
                                <StyledTableCell key="volume" align="right"><Trans>Volume (m&#xB3;)</Trans></StyledTableCell>
                                <StyledTableCell align="right"><Trans>Price</Trans></StyledTableCell>
                                <StyledTableCell align="right"><Trans>Row Total</Trans></StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody key="body">
                            {groupedData.items
                                .sort((a, b) => a.cutting.name.localeCompare(b.cutting.name))
                                .map((cuttingRow) =>
                                    <React.Fragment key={'fragment' + cuttingRow.cutting.id}>
                                    {cuttingRow.items
                                        .sort((a, b) => a.storage.name.localeCompare(b.storage.name))
                                        .map((storageRow) =>
                                            <React.Fragment key={'fragment' + storageRow.storage.id}>
                                                {storageRow.items
                                                    .filter(a => !!(a.items))
                                                    .sort((a, b) => a.timberType.name.localeCompare(b.timberType.name) || a.timberClass.name.localeCompare(b.timberClass.name))
                                                    .map((groupedRow, groupedRowIndex) =>
                                                        <React.Fragment
                                                            key={'fragment' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                            {groupedRow.items && groupedRow.items
                                                                .sort((a, b) => (a.thicknessClass.minDiameter || 0) - (b.thicknessClass.minDiameter || 0))
                                                                .map((itemRow, itemRowIndex) =>
                                                                    itemRowIndex === 0 && groupedRowIndex === 0 ?
                                                                        <TableRow
                                                                            key={storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id + itemRow.thicknessClass.id}>
                                                                            <StyledTableStorage
                                                                                key="storage-id">{storageRow.storage.name}</StyledTableStorage>
                                                                            <StyledTableTypeClass
                                                                                key="type-id">{groupedRow.timberType.name}</StyledTableTypeClass>
                                                                            <StyledTableTypeClass
                                                                                key="class-id">{groupedRow.timberClass.name}</StyledTableTypeClass>
                                                                            <TableCell
                                                                                key="thickness-class-id">{itemRow.thicknessClass.name}</TableCell>
                                                                            <TableCell key="count"
                                                                                       align="right">{itemRow.count}</TableCell>
                                                                            <TableCell key="volume"
                                                                                       align="right">{fixedFormat(itemRow.sum)}</TableCell>

                                                                            <TableCell align="right" sx={{minWidth: 125}}>
                                                                                <NumericPriceInput
                                                                                    value={overrides[itemRow.key] ?? (itemRow.price ?? '')}
                                                                                    onChange={(value) => handlePriceChange(itemRow.key, value)}
                                                                                    originalValue={ basePriceMap.get(itemRow.key) && basePriceMap.get(itemRow.key) }
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell align="right" sx={{minWidth: 120}}>{itemRow.amount != null ? fixedFormat(itemRow.amount) : '-'}</TableCell>
                                                                        </TableRow>
                                                                        :
                                                                        itemRowIndex === 0 ?
                                                                            <TableRow
                                                                                key={storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id + itemRow.thicknessClass.id}>
                                                                                <StyledTableStorage
                                                                                    key="storage-id"></StyledTableStorage>
                                                                                <StyledTableTypeClass
                                                                                    key="type-id">{groupedRow.timberType.name}</StyledTableTypeClass>
                                                                                <StyledTableTypeClass
                                                                                    key="class-id">{groupedRow.timberClass.name}</StyledTableTypeClass>
                                                                                <TableCell
                                                                                    key="thickness-class-id">{itemRow.thicknessClass.name}</TableCell>
                                                                                <TableCell key="count"
                                                                                           align="right">{itemRow.count}</TableCell>
                                                                                <TableCell key="volume"
                                                                                           align="right">{fixedFormat(itemRow.sum)}</TableCell>

                                                                                <TableCell align="right" sx={{minWidth: 125}}>
                                                                                    <NumericPriceInput
                                                                                        value={overrides[itemRow.key] ?? (itemRow.price ?? '')}
                                                                                        onChange={(value) => handlePriceChange(itemRow.key, value)}
                                                                                        originalValue={ basePriceMap.get(itemRow.key) && basePriceMap.get(itemRow.key) }
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell align="right" sx={{minWidth: 120}}>{itemRow.amount != null ? fixedFormat(itemRow.amount) : '-'}</TableCell>
                                                                            </TableRow>
                                                                            :
                                                                            <TableRow
                                                                                key={storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id + itemRow.thicknessClass.id}>
                                                                                <StyledTableStorage
                                                                                    key="storage-id"></StyledTableStorage>
                                                                                <StyledTableTypeClass
                                                                                    key="type-id"></StyledTableTypeClass>
                                                                                <StyledTableTypeClass
                                                                                    key="class-id"></StyledTableTypeClass>
                                                                                <TableCell
                                                                                    key="thickness-class-id">{itemRow.thicknessClass.name}</TableCell>
                                                                                <TableCell key="count"
                                                                                           align="right">{itemRow.count}</TableCell>
                                                                                <TableCell key="volume"
                                                                                           align="right">{fixedFormat(itemRow.sum)}</TableCell>

                                                                                <TableCell align="right" sx={{minWidth: 125}}>
                                                                                    <NumericPriceInput
                                                                                        value={overrides[itemRow.key] ?? (itemRow.price ?? '')}
                                                                                        onChange={(value) => handlePriceChange(itemRow.key, value)}
                                                                                        originalValue={ basePriceMap.get(itemRow.key) && basePriceMap.get(itemRow.key) }
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell align="right" sx={{minWidth: 120}}>{itemRow.amount != null ? fixedFormat(itemRow.amount) : '-'}</TableCell>
                                                                            </TableRow>
                                                                )}
                                                            <TableRow
                                                                key={'total' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                                <StyledTableStorage
                                                                    key="empty"></StyledTableStorage>
                                                                <StyledTableCell key="total" colSpan={3}>
                                                                    <Trans>Total class and type</Trans> ({groupedRow.timberType.name}, {groupedRow.timberClass.name})
                                                                </StyledTableCell>
                                                                <StyledTableCell key="count"
                                                                                 align="right">{groupedRow.count}</StyledTableCell>
                                                                <StyledTableCell key="sum"
                                                                                 align="right">{fixedFormat(groupedRow.sum)}</StyledTableCell>
                                                                <StyledTableCell align="right" sx={{minWidth: 120}}></StyledTableCell>
                                                                <StyledTableCell align="right" sx={{minWidth: 120}}>{groupedRow.amount != null ? fixedFormat(groupedRow.amount) : '-'}</StyledTableCell>
                                                            </TableRow>
                                                        </React.Fragment>
                                                    )}

                                                {storageRow.items
                                                    .filter(a => !(a.items))
                                                    .sort((a, b) => a.timberType.name.localeCompare(b.timberType.name) || a.timberClass.name.localeCompare(b.timberClass.name))
                                                    .map((groupedRow, groupedRowIndex) =>
                                                        <React.Fragment
                                                            key={'fragment' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                            <TableRow
                                                                key={'total' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                                <StyledTableStorage
                                                                    key="empty"></StyledTableStorage>
                                                                <StyledTableCell key="total" colSpan={3}>
                                                                    <Trans>Total class and
                                                                        type</Trans> ({groupedRow.timberType.name}, {groupedRow.timberClass.name})
                                                                </StyledTableCell>
                                                                <StyledTableCell key="count"
                                                                                 align="right">{groupedRow.count}</StyledTableCell>
                                                                <StyledTableCell key="sum"
                                                                                 align="right">{fixedFormat(groupedRow.sum)}</StyledTableCell>
                                                                <StyledTableCell align="right" sx={{minWidth: 125}}>
                                                                    <NumericPriceInput
                                                                        value={overrides[groupedRow.key] ?? (groupedRow.price ?? '')}
                                                                        onChange={(value) => handlePriceChange(groupedRow.key, value)}
                                                                        originalValue={ basePriceMap.has(groupedRow.key) && basePriceMap.get(groupedRow.key) }
                                                                    />
                                                                </StyledTableCell>
                                                                <StyledTableCell align="right" sx={{minWidth: 120}}>{groupedRow.amount != null ? fixedFormat(groupedRow.amount) : '-'}</StyledTableCell>
                                                            </TableRow>
                                                        </React.Fragment>
                                                    )}
                                                <TableRow key={'total' + storageRow.storage.id}>
                                                    <StyledTableStorageTotal key="total" colSpan={4}>
                                                        <Trans>Total on storage</Trans> ({storageRow.storage.name})
                                                    </StyledTableStorageTotal>
                                                    <StyledTableStorageTotal key="count"
                                                                             align="right">{storageRow.count}</StyledTableStorageTotal>
                                                    <StyledTableStorageTotal key="sum"
                                                                             align="right">{fixedFormat(storageRow.sum)}</StyledTableStorageTotal>
                                                    <StyledTableStorageTotal align="right" sx={{minWidth: 120}}></StyledTableStorageTotal>
                                                    <StyledTableStorageTotal align="right" sx={{minWidth: 120}}>{storageRow.amount != null ? fixedFormat(storageRow.amount) : '-'}</StyledTableStorageTotal>
                                                </TableRow>
                                            </React.Fragment>
                                        )
                                    }
                                    </React.Fragment>
                                )
                            }
                            <TableRow key="Total">
                                <StyledTableFooter key="total" align="right"
                                                   colSpan={4}><Trans>Total</Trans></StyledTableFooter>
                                <StyledTableFooter key="count"
                                                   align="right">{groupedData.count}</StyledTableFooter>
                                <StyledTableFooter key="sum"
                                                   align="right">{fixedFormat(groupedData.sum)}</StyledTableFooter>
                                <StyledTableFooter align="right" />
                                <StyledTableFooter align="right">{fixedFormat(totalAmount)}</StyledTableFooter>
                            </TableRow>
                            <TableRow key="AdjustTotal">
                                <TableCell key="adjusttotal" colSpan={7} align="right">
                                    <Button variant="outlined" onClick={reCalculatePrices}>
                                        <Trans>Adjust prices to the target total amount</Trans>
                                    </Button>
                                </TableCell>
                                <StyledTableFooter align="right" sx={{minWidth: 120}}>
                                    <NumericPriceInput
                                        value={newTotalAmount}
                                        onChange={setNewTotalAmount}
                                    />
                                </StyledTableFooter>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            }

                <Dialog open={newProfileOpen} onClose={() => setNewProfileOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle><Trans>Create new pricing profile</Trans></DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{mt: 1}}>
                            <TextField
                                label={t`Name`}
                                value={newProfileName}
                                onChange={(e) => setNewProfileName(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label={t`Description`}
                                value={newProfileDescription}
                                onChange={(e) => setNewProfileDescription(e.target.value)}
                                multiline
                                minRows={2}
                                fullWidth
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setNewProfileOpen(false)}><Trans>Cancel</Trans></Button>
                        <Button variant="contained" onClick={saveAsNewProfile}><Trans>Save</Trans></Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </Paper>
    );
}

export default PriceCalculator;
